import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, MessageCircle, Newspaper, Calendar, BookOpen, FlaskConical, Users, CheckCheck, Inbox } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ICONS = {
  message: { icon: MessageCircle, cls: 'bg-blue-100 text-blue-600' },
  news: { icon: Newspaper, cls: 'bg-amber-100 text-amber-700' },
  event: { icon: Calendar, cls: 'bg-emerald-100 text-emerald-700' },
  course: { icon: BookOpen, cls: 'bg-sky-100 text-sky-700' },
  britelab: { icon: FlaskConical, cls: 'bg-violet-100 text-violet-700' },
  britelab_new_post: { icon: FlaskConical, cls: 'bg-violet-100 text-violet-700' },
  britelab_new_comment: { icon: MessageCircle, cls: 'bg-violet-100 text-violet-700' },
  britelab_comment_on_post: { icon: MessageCircle, cls: 'bg-violet-100 text-violet-700' },
  britelab_mention: { icon: Users, cls: 'bg-violet-100 text-violet-700' },
  forum_mention: { icon: Users, cls: 'bg-amber-100 text-amber-700' },
  britelab_milestones_updated: { icon: FlaskConical, cls: 'bg-violet-100 text-violet-700' },
  britelab_research_uploaded: { icon: FlaskConical, cls: 'bg-violet-100 text-violet-700' },
  member: { icon: Users, cls: 'bg-rose-100 text-rose-700' },
};

// Filter dropdown matches certain types based on prefix
const TYPE_GROUPS = {
  britelab: (t) => t === 'britelab' || (t || '').startsWith('britelab_'),
  message: (t) => t === 'message',
  news: (t) => t === 'news',
  event: (t) => t === 'event',
  course: (t) => t === 'course',
  member: (t) => t === 'member',
};

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'message', label: 'Messages' },
  { value: 'news', label: 'News' },
  { value: 'event', label: 'Events' },
  { value: 'course', label: 'Courses' },
  { value: 'britelab', label: 'BriteLabs' },
  { value: 'member', label: 'Members' },
];

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function NotificationsPage({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const token = typeof window !== 'undefined'
    ? (sessionStorage.getItem('session_token') || localStorage.getItem('sll_session_token'))
    : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = () => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/user-notifs?limit=200`, { credentials: 'include', headers })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setItems(d.notifications || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const markAllRead = async () => {
    await fetch(`${BACKEND_URL}/api/user-notifs/read`, { method: 'POST', credentials: 'include', headers });
    setItems(prev => prev.map(n => ({ ...n, read: true })));
  };

  const markOneRead = async (notif) => {
    if (notif.read) return;
    await fetch(`${BACKEND_URL}/api/user-notifs/${notif.notification_id}/read`, { method: 'POST', credentials: 'include', headers });
    setItems(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n));
  };

  const handleClick = (notif) => {
    markOneRead(notif);
    if (notif.link) {
      if (notif.type === 'news' && notif.link.startsWith('http')) {
        window.open(notif.link, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = notif.link;
      }
    }
  };

  const filtered = items.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.read;
    const matcher = TYPE_GROUPS[filter];
    return matcher ? matcher(n.type) : n.type === filter;
  });

  const unreadTotal = items.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-24 lg:pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-navy/10 flex items-center justify-center">
                <Bell className="h-5 w-5 text-brand-navy" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold font-heading text-slate-800">Notifications</h1>
                <p className="text-xs text-slate-500" data-testid="notifications-counts">
                  {unreadTotal > 0 ? `${unreadTotal} unread · ${items.length} total` : `${items.length} total`}
                </p>
              </div>
            </div>
            {unreadTotal > 0 && (
              <Button onClick={markAllRead} variant="outline" size="sm" data-testid="notifications-mark-all-read">
                <CheckCheck className="h-4 w-4 mr-1.5" /> Mark all read
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="notifications-filters">
            {FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                data-testid={`notifications-filter-${f.value}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  filter === f.value
                    ? 'bg-brand-navy text-white border-brand-navy'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-brand-navy/40'
                }`}
              >
                {f.label}
                {f.value === 'unread' && unreadTotal > 0 && (
                  <span className={`ml-1.5 ${filter === f.value ? 'opacity-90' : 'text-red-500 font-bold'}`}>{unreadTotal}</span>
                )}
              </button>
            ))}
          </div>

          {loading ? (
            <Card className="p-12 text-center text-sm text-slate-400" data-testid="notifications-loading">Loading…</Card>
          ) : filtered.length === 0 ? (
            <Card className="p-12 text-center" data-testid="notifications-empty">
              <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">
                {filter === 'all' ? "You're all caught up — no notifications yet." : 'Nothing in this category.'}
              </p>
              {filter !== 'all' && (
                <Button onClick={() => setFilter('all')} variant="outline" size="sm" className="mt-4">View all</Button>
              )}
            </Card>
          ) : (
            <Card className="divide-y divide-slate-100 overflow-hidden" data-testid="notifications-list">
              {filtered.map(notif => {
                const I = ICONS[notif.type] || { icon: Bell, cls: 'bg-slate-100 text-slate-600' };
                const Icon = I.icon;
                return (
                  <button
                    key={notif.notification_id}
                    onClick={() => handleClick(notif)}
                    data-testid={`notification-row-${notif.notification_id}`}
                    className={`w-full text-left px-4 py-3.5 hover:bg-slate-50 transition-colors flex gap-3 ${!notif.read ? 'bg-brand-navy/5' : ''}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${I.cls}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-tight ${!notif.read ? 'font-semibold text-slate-800' : 'font-medium text-slate-700'}`}>
                        {notif.title}
                      </p>
                      {notif.body && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{notif.body}</p>}
                      <p className="text-[10px] text-slate-400 mt-1.5 uppercase tracking-wider">
                        {notif.type} &middot; {timeAgo(notif.created_at)}
                      </p>
                    </div>
                    {!notif.read && <div className="w-2 h-2 bg-brand-navy rounded-full flex-shrink-0 mt-2" />}
                  </button>
                );
              })}
            </Card>
          )}

          <div className="text-center mt-6">
            <Link to="/progress" className="text-xs text-slate-500 hover:text-brand-navy">&larr; Back to progress</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
